import maplibregl from "maplibre-gl";
import { filterByDate } from "@openhistoricalmap/maplibre-gl-dates";
import "maplibre-gl/dist/maplibre-gl.css";

const historicalStyle = "https://www.openhistoricalmap.org/map-styles/main/main.json";
const INITIAL_YEAR = 117;
const fallbackStyle = {
  version: 8 as const,
  sources: {},
  layers: [{ id: "historical-layer-unavailable", type: "background" as const, paint: { "background-color": "#171914" } }]
};

interface ApiLayer {
  id: string;
  name: string;
  description: string;
  color: string;
  sort_order: number;
}

interface ApiEvent {
  id: string;
  layer_id: string;
  layer_name: string;
  layer_color: string;
  title: string;
  event_kind: string;
  start_year: number;
  end_year: number | null;
  map_end_year: number;
  precision: string;
  record_type: "event" | "war";
  war_id: string | null;
  war_title: string | null;
  battle_count?: number;
  summary: string;
  location_name: string;
  longitude: number;
  latitude: number;
  confidence: string;
  source_title: string | null;
  source_url: string | null;
  source_publisher: string | null;
}

interface EventCluster {
  key: string;
  records: ApiEvent[];
  longitude: number;
  latitude: number;
  color: string;
  latestStartYear: number;
}

interface PopulationPoint {
  year: number;
  population: number;
}

interface PopulationResponse {
  points: PopulationPoint[];
  live: boolean;
  source: { title: string; url: string; publisher: string; license: string; detail: string };
}

interface RecentPassageNotification {
  element: HTMLElement;
  dismissTimer?: number;
  removeTimer?: number;
}

interface TransientMapTooltip {
  popup: maplibregl.Popup;
  dismissTimer: number;
}

function required<T extends Element>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element as unknown as T;
}

const yearInput = required<HTMLInputElement>("year-range");
const yearOutput = required<HTMLOutputElement>("year-output");
const timelineNote = required<HTMLElement>("timeline-note");
const bordersToggle = required<HTMLInputElement>("borders-layer");
const labelNativeButton = required<HTMLButtonElement>("label-native-button");
const labelEnglishButton = required<HTMLButtonElement>("label-english-button");
const eventLayers = required<HTMLElement>("event-layers");
const recordCard = required<HTMLElement>("record-card");
const dataDesk = required<HTMLElement>("data-desk");
const eventDialog = required<HTMLDialogElement>("event-dialog");
const eventForm = required<HTMLFormElement>("event-form");
const eventLayerSelect = required<HTMLSelectElement>("event-layer-select");
const eventWarSelect = required<HTMLSelectElement>("event-war-select");
const warFormField = required<HTMLElement>("war-form-field");
const eventKindSelect = required<HTMLSelectElement>("event-kind-select");
const lifeKindFormField = required<HTMLElement>("life-kind-form-field");
const eventEndYear = required<HTMLInputElement>("event-end-year");
const formError = required<HTMLElement>("event-form-error");
// const mapStatus = required<HTMLElement>("map-status-text");
const yearJumpTrigger = required<HTMLButtonElement>("year-jump-trigger");
const yearJumpInput = required<HTMLInputElement>("year-jump-input");
const trackTimeline = required<SVGSVGElement>("track-timeline");
const trackTimelineLabels = required<HTMLElement>("track-timeline-labels");
const timelineWindowOutput = required<HTMLOutputElement>("timeline-window-output");
const populationOutput = required<HTMLElement>("population-output");
const populationSource = required<HTMLAnchorElement>("population-source");
const recentPassageLog = required<HTMLElement>("recent-passage-log");
const TIMELINE_MIN_YEAR = Number(yearInput.min);
const TIMELINE_MAX_YEAR = Number(yearInput.max);
// The single playback-clock constant: one visual segment lasts this long,
// regardless of map/network work completing late.
const PLAYBACK_YEAR_DURATION_MS = 950;
const MAX_RECENT_PASSAGE_ENTRIES = 40;
const RECENT_PASSAGE_VISIBLE_MS = 5000;
const MAX_TRANSIENT_MAP_TOOLTIPS = 3;
const MAX_TRANSIENT_MAP_TOOLTIPS_PER_UPDATE = 3;
const COLOCATED_COORD_PRECISION = 3;
const SPIDER_RADIUS_PX = 42;
const SPIDER_HOVER_RADIUS_PX = 76;
const SPIDER_ANIMATION_MS = 180;
const isCoarsePointer = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
const TAP_RADIUS_SCALE = isCoarsePointer ? 1.8 : 1;
function tapRadius(...zoomRadiusPairs: number[]): any {
  const stops: number[] = [];
  for (let i = 0; i < zoomRadiusPairs.length; i += 2) stops.push(zoomRadiusPairs[i], zoomRadiusPairs[i + 1] * TAP_RADIUS_SCALE);
  return ["interpolate", ["linear"], ["zoom"], ...stops];
}
let timelineStart = Math.max(TIMELINE_MIN_YEAR, INITIAL_YEAR - 1000);
let timelineEnd = Math.min(TIMELINE_MAX_YEAR, INITIAL_YEAR + 1000);
yearInput.min = String(timelineStart);
yearInput.max = String(timelineEnd);
yearInput.value = String(INITIAL_YEAR);

function formatYear(year: number) { return year < 0 ? `${Math.abs(year)} BCE` : `${year} CE`; }
function recordTypeLabel(record: ApiEvent) {
  if (record.record_type === "war") return "War";
  if (record.event_kind === "battle") return "Battle";
  if (record.event_kind === "birth") return "Birth";
  if (record.event_kind === "death") return "Death";
  if (record.event_kind === "invention") return "Invention";
  return record.layer_name;
}
function recordTooltipMeta(record: ApiEvent) {
  if (record.event_kind === "battle") return record.war_title ? `Battle · ${record.war_title}` : "Battle · war ungrouped";
  return recordTypeLabel(record);
}
function overlapsYear(event: ApiEvent, year: number): boolean {
  return event.start_year <= year && event.map_end_year >= year;
}

function isLongRunningRecord(record: ApiEvent) {
  return record.record_type === "war" || record.layer_id === "empire" || (record.end_year !== null && record.end_year !== record.start_year);
}

function passageBoundaries(record: ApiEvent) {
  if (!isLongRunningRecord(record)) return [{ key: `${record.id}:point:${record.start_year}`, label: record.title, year: record.start_year, recordId: record.id }];
  const boundaries = [{ key: `${record.id}:start:${record.start_year}`, label: `Beginning of ${record.title}`, year: record.start_year, recordId: record.id }];
  if (record.end_year !== null && record.end_year !== record.start_year) {
    boundaries.push({ key: `${record.id}:end:${record.end_year}`, label: `End of ${record.title}`, year: record.end_year, recordId: record.id });
  }
  return boundaries;
}

function englishTextField(nativeTextField: unknown) {
  return [
    "coalesce",
    ["get", "name:en"],
    ["get", "name_en"],
    ["get", "name:eng"],
    ["get", "name"],
    ["get", "name:latin"],
    nativeTextField ?? ""
  ];
}

/**
 * Some OpenHistoricalMap tiles encode their decimal dates as strings even
 * though the style/date helper compares them numerically. MapLibre rejects
 * that mixed comparison while scrubbing. Coerce only those two OHM fields.
 */
function coerceHistoricalDateFields(expression: unknown): unknown {
  if (!Array.isArray(expression)) return expression;
  if (expression.length === 2 && expression[0] === "get" && (expression[1] === "start_decdate" || expression[1] === "end_decdate")) {
    return ["to-number", expression];
  }
  return expression.map(coerceHistoricalDateFields);
}

function filterHistoricalMapByYear(year: number) {
  filterByDate(map, String(year));
  if (historicalDateFiltersNormalised) return;
  for (const layer of map.getStyle().layers ?? []) {
    if (!("source-layer" in layer)) continue;
    const filter = map.getFilter(layer.id);
    if (filter) map.setFilter(layer.id, coerceHistoricalDateFields(filter) as any);
  }
  historicalDateFiltersNormalised = true;
}

let layers: ApiLayer[] = [];
let events: ApiEvent[] = [];
let wars: ApiEvent[] = [];
let populationPoints: PopulationPoint[] = [];
const recentPassageNotifications = new Map<string, RecentPassageNotification>();
const transientMapTooltips = new Map<string, TransientMapTooltip>();
let selectedRecordId: string | null = null;
let activeSpiderClusterKey: string | null = null;
let currentVisibleMapRecords: ApiEvent[] = [];
let currentEventClusters: EventCluster[] = [];
let renderedSpiderCluster: EventCluster | undefined;
let spiderAnimationProgress = 0;
let spiderAnimationFrame: number | undefined;
let mapLabelLanguage: "native" | "english" = "native";
const visibleLayerIds = new Set<string>();
const nativeTextFields = new Map<string, unknown>();
let timelineNeedsRender = true;
let timelinePlayhead: SVGLineElement | undefined;
let timelinePlayheadCap: SVGPathElement | undefined;
let lastMapYear: number | undefined;
let lastVisibleRecordSignature: string | undefined;
let lastVisibleRecordIds = new Set<string>();
let lastSelectionId: string | null | undefined;
let lastBordersVisible: boolean | undefined;
let historicalDateFiltersNormalised = false;

const svgNamespace = "http://www.w3.org/2000/svg";
function svgElement<K extends keyof SVGElementTagNameMap>(tag: K, attributes: Record<string, string>) {
  const element = document.createElementNS(svgNamespace, tag);
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  return element;
}

function formatPopulation(value: number) {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(value >= 10_000_000_000 ? 0 : 2).replace(/\.00$/, "")} billion`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 100_000_000 ? 0 : 1).replace(/\.0$/, "")} million`;
  return new Intl.NumberFormat("en-US").format(Math.round(value));
}

function populationForYear(year: number) {
  if (!populationPoints.length || year < populationPoints[0].year || year > populationPoints.at(-1)!.year) return null;
  const exact = populationPoints.find((point) => point.year === year);
  if (exact) return { population: exact.population, interpolated: false };
  let afterIndex = populationPoints.findIndex((point) => point.year > year);
  if (afterIndex < 1) return null;
  const before = populationPoints[afterIndex - 1];
  const after = populationPoints[afterIndex];
  const progress = (year - before.year) / (after.year - before.year);
  return { population: before.population + (after.population - before.population) * progress, interpolated: true };
}

function removeRecentPassageNotification(key: string, animate = true) {
  const notification = recentPassageNotifications.get(key);
  if (!notification) return;
  if (notification.dismissTimer !== undefined) window.clearTimeout(notification.dismissTimer);
  if (notification.removeTimer !== undefined) window.clearTimeout(notification.removeTimer);
  if (!animate) {
    notification.element.remove();
    recentPassageNotifications.delete(key);
    return;
  }
  notification.element.classList.add("leaving");
  notification.removeTimer = window.setTimeout(() => {
    notification.element.remove();
    recentPassageNotifications.delete(key);
  }, 360);
}

function focusRecentPassageRecord(recordId: string) {
  const record = events.find((candidate) => candidate.id === recordId);
  if (record) selectRecord(record);
}

function pushRecentPassageNotification(boundary: { key: string; label: string; year: number; recordId: string }) {
  removeRecentPassageNotification(boundary.key, false);
  const item = document.createElement("div");
  item.className = "recent-passage-item";
  item.tabIndex = 0;
  item.role = "button";
  const year = document.createElement("span");
  year.className = "recent-passage-year";
  year.textContent = formatYear(boundary.year);
  const label = document.createElement("span");
  label.className = "recent-passage-label";
  label.textContent = boundary.label;
  item.append(year, label);
  item.title = `${boundary.label} (${formatYear(boundary.year)})`;
  item.addEventListener("click", () => focusRecentPassageRecord(boundary.recordId));
  item.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    focusRecentPassageRecord(boundary.recordId);
  });
  recentPassageLog.prepend(item);

  const notification: RecentPassageNotification = { element: item };
  notification.dismissTimer = window.setTimeout(() => removeRecentPassageNotification(boundary.key), RECENT_PASSAGE_VISIBLE_MS);
  recentPassageNotifications.set(boundary.key, notification);

  while (recentPassageLog.children.length > MAX_RECENT_PASSAGE_ENTRIES) {
    const oldest = recentPassageLog.lastElementChild as HTMLElement | null;
    const oldestKey = [...recentPassageNotifications].find(([, notification]) => notification.element === oldest)?.[0];
    if (!oldestKey) break;
    removeRecentPassageNotification(oldestKey, false);
  }
}

function removeTransientMapTooltip(key: string) {
  const tooltip = transientMapTooltips.get(key);
  if (!tooltip) return;
  window.clearTimeout(tooltip.dismissTimer);
  tooltip.popup.remove();
  transientMapTooltips.delete(key);
}

function clearTransientMapTooltips() {
  for (const key of [...transientMapTooltips.keys()]) removeTransientMapTooltip(key);
}

function tooltipContent(record: ApiEvent) {
  const content = document.createElement("div");
  const title = document.createElement("strong");
  title.textContent = record.title;
  const meta = document.createElement("span");
  meta.textContent = recordTooltipMeta(record);
  content.append(title, meta);
  return content;
}

function renderedCoordinateForRecord(record: ApiEvent): [number, number] {
  const cluster = currentEventClusters.find((candidate) => candidate.records.some((clusterRecord) => clusterRecord.id === record.id));
  if (!cluster) return [record.longitude, record.latitude];
  if (renderedSpiderCluster?.key === cluster.key) {
    const index = cluster.records.findIndex((clusterRecord) => clusterRecord.id === record.id);
    if (index >= 0) return spiderCoordinate(cluster, index, easeOutCubic(spiderAnimationProgress));
  }
  return [cluster.longitude, cluster.latitude];
}

function showTransientMapTooltip(record: ApiEvent, year: number) {
  if (!styleReady) return;
  const key = `${record.id}:${year}`;
  removeTransientMapTooltip(key);
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 14,
    className: "map-event-tooltip transient-map-event-tooltip"
  })
    .setLngLat(renderedCoordinateForRecord(record))
    .setDOMContent(tooltipContent(record))
    .addTo(map);
  const dismissTimer = window.setTimeout(() => removeTransientMapTooltip(key), RECENT_PASSAGE_VISIBLE_MS);
  transientMapTooltips.set(key, { popup, dismissTimer });

  while (transientMapTooltips.size > MAX_TRANSIENT_MAP_TOOLTIPS) {
    const oldestKey = transientMapTooltips.keys().next().value;
    if (!oldestKey) break;
    removeTransientMapTooltip(oldestKey);
  }
}

function recordRecentlyPassed(previousYear: number, nextYear: number) {
  if (!events.length || previousYear === nextYear) return;
  const direction = nextYear > previousYear ? 1 : -1;
  const passed = events
    .filter((record) => visibleLayerIds.has(record.layer_id))
    .filter((record) => direction > 0
      ? record.start_year > previousYear && record.start_year <= nextYear
      : record.map_end_year < previousYear && record.map_end_year >= nextYear)
    .sort((a, b) => direction > 0 ? a.start_year - b.start_year : b.map_end_year - a.map_end_year);

  for (const record of passed) showTransientMapTooltip(record, direction > 0 ? record.start_year : record.map_end_year);
}

const TRACK_GUTTER = 8;
const TRACK_WIDTH = 1192;

function trackX(year: number) {
  return TRACK_GUTTER + ((year - timelineStart) / (timelineEnd - timelineStart)) * TRACK_WIDTH;
}

function renderTimelineTracks(year: number) {
  if (!timelineNeedsRender) {
    updateTimelinePlayhead(year);
    return;
  }
  const rows = layers.map((layer) => ({ id: layer.id, name: layer.name, color: layer.color }));
  const rowHeight = 27;
  const trackHeight = Math.max(108, rows.length * rowHeight);
  trackTimeline.setAttribute("viewBox", `0 0 1200 ${trackHeight}`);
  trackTimeline.style.height = `${trackHeight}px`;
  trackTimelineLabels.style.height = `${trackHeight}px`;
  const children: SVGElement[] = [];
  for (let tick = 0; tick <= 4; tick += 1) {
    const x = TRACK_GUTTER + (TRACK_WIDTH / 4) * tick;
    children.push(svgElement("line", { x1: x.toFixed(2), x2: x.toFixed(2), y1: "0", y2: String(trackHeight), class: "track-tick" }));
  }
  const labelRows: HTMLElement[] = [];
  rows.forEach((row, index) => {
    const y = index * rowHeight;
    children.push(svgElement("rect", { x: "0", y: String(y), width: "1200", height: String(rowHeight - 2), class: "track-lane" }));
    const records = events.filter((event) => event.layer_id === row.id && event.start_year <= timelineEnd && event.map_end_year >= timelineStart);
    const labelRow = document.createElement("div");
    labelRow.className = "track-label-row";
    labelRow.style.height = `${rowHeight - 2}px`;
    const labelName = document.createElement("span");
    labelName.className = "track-label";
    labelName.textContent = row.name;
    const labelCount = document.createElement("span");
    labelCount.className = "track-count";
    labelCount.textContent = String(records.length);
    labelRow.append(labelName, labelCount);
    labelRows.push(labelRow);
    records.forEach((event) => {
      const start = Math.max(event.start_year, timelineStart);
      const end = Math.min(event.map_end_year, timelineEnd);
      const x = trackX(start);
      const width = Math.max(3, trackX(end) - x + 1.5);
      const className = ["track-event", !visibleLayerIds.has(row.id) ? "inactive" : "", selectedRecordId === event.id ? "selected" : ""].filter(Boolean).join(" ");
      children.push(svgElement("rect", {
        x: x.toFixed(2), y: String(y + 7), width: width.toFixed(2), height: "12", rx: "1", fill: row.color, class: className,
        "data-event-id": event.id, role: "button", tabindex: "0", "aria-label": `View ${event.title}`
      }));
    });
  });
  const playheadX = trackX(year);
  const playhead = svgElement("line", { x1: playheadX.toFixed(2), x2: playheadX.toFixed(2), y1: "0", y2: String(trackHeight), class: "track-playhead" }) as SVGLineElement;
  const playheadCap = svgElement("path", { d: `M ${playheadX - 4} 0 L ${playheadX + 4} 0 L ${playheadX} 6 Z`, class: "track-playhead-cap" }) as SVGPathElement;
  children.push(playhead, playheadCap);
  trackTimeline.replaceChildren(...children);
  trackTimelineLabels.replaceChildren(...labelRows);
  timelinePlayhead = playhead;
  timelinePlayheadCap = playheadCap;
  timelineNeedsRender = false;
  timelineWindowOutput.value = `${formatYear(timelineStart)} — ${formatYear(timelineEnd)} · ${timelineEnd - timelineStart} years`;
  const selected = populationForYear(year);
  populationOutput.textContent = selected ? `${formatPopulation(selected.population)}${selected.interpolated ? " · estimate" : ""}` : "No estimate";
}

function updateTimelinePlayhead(year: number) {
  const x = trackX(year);
  timelinePlayhead?.setAttribute("x1", x.toFixed(2));
  timelinePlayhead?.setAttribute("x2", x.toFixed(2));
  timelinePlayheadCap?.setAttribute("d", `M ${x - 4} 0 L ${x + 4} 0 L ${x} 6 Z`);
}

// MapLibre normally starts one worker. Tile decoding and style processing are
// worker-safe, so use a small bounded pool without monopolising the machine.
maplibregl.setWorkerCount(Math.min(4, Math.max(2, Math.floor((navigator.hardwareConcurrency || 4) / 2))));

const map = new maplibregl.Map({
  container: "map",
  style: historicalStyle,
  center: [18, 25],
  zoom: 1.4,
  minZoom: 1,
  maxZoom: 8,
  attributionControl: false,
  // Keep substantially more recently visited tiles in memory. The basemap is
  // static tile data; changing years only changes its client-side filter.
  maxTileCacheSize: 512,
  maxTileCacheZoomLevels: 10,
  fadeDuration: 0,
  crossSourceCollisions: false,
  transformRequest: (url) => ({ url, collectResourceTiming: false })
});
map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");

let isFallbackMap = false;
let eventInteractionsAdded = false;
// map.isStyleLoaded() also waits for every tile in the current viewport to
// finish loading, which the OHM live style rarely satisfies. We only need to
// know that the style itself (and our custom layers) are in place.
let styleReady = false;
const mapTooltip = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 14, className: "map-event-tooltip" });

function applyMapLabelLanguage() {
  if (!styleReady) return;
  for (const layer of map.getStyle().layers ?? []) {
    if (layer.type !== "symbol") continue;
    const textField = map.getLayoutProperty(layer.id, "text-field");
    if (textField === undefined) continue;
    if (!nativeTextFields.has(layer.id)) nativeTextFields.set(layer.id, textField);
    const nativeTextField = nativeTextFields.get(layer.id);
    map.setLayoutProperty(layer.id, "text-field", mapLabelLanguage === "english" ? englishTextField(nativeTextField) : nativeTextField);
  }
  labelNativeButton.classList.toggle("active", mapLabelLanguage === "native");
  labelNativeButton.setAttribute("aria-pressed", String(mapLabelLanguage === "native"));
  labelEnglishButton.classList.toggle("active", mapLabelLanguage === "english");
  labelEnglishButton.setAttribute("aria-pressed", String(mapLabelLanguage === "english"));
}

map.on("error", (event) => {
  if (!isFallbackMap && !styleReady) {
    isFallbackMap = true;
    // mapStatus.textContent = "OpenHistoricalMap unavailable · no anachronistic fallback shown";
    map.setStyle(fallbackStyle);
    console.warn("OpenHistoricalMap style could not load", event.error);
  }
});

map.on("style.load", () => {
  // A style reload removes the custom sources, so their cached render state is
  // no longer valid.
  styleReady = false;
  lastMapYear = undefined;
  lastVisibleRecordSignature = undefined;
  lastVisibleRecordIds = new Set();
  lastSelectionId = undefined;
  lastBordersVisible = undefined;
  historicalDateFiltersNormalised = false;
  clearTransientMapTooltips();
  // mapStatus.textContent = isFallbackMap
  //   ? "OpenHistoricalMap unavailable · no anachronistic fallback shown"
  //   : "OpenHistoricalMap · date-filtered historical geography";
  map.addSource("historical-events", { type: "geojson", data: featureCollection([]) });
  map.addLayer({
    id: "historical-events-halo",
    type: "circle",
    source: "historical-events",
    paint: { "circle-radius": tapRadius(1, 7, 4, 13, 7, 19), "circle-color": ["get", "color"], "circle-opacity": 0.13, "circle-blur": 0.2 },
    layout: { "circle-sort-key": ["get", "startYear"] }
  });
  map.addLayer({
    id: "historical-events",
    type: "circle",
    source: "historical-events",
    paint: { "circle-radius": tapRadius(1, 3, 4, 5, 7, 7), "circle-color": ["get", "color"], "circle-stroke-width": tapRadius(1, 0.75, 4, 1.1, 7, 1.5), "circle-stroke-color": "#f5e7d4" },
    layout: { "circle-sort-key": ["get", "startYear"] }
  });
  map.addSource("historical-event-clusters", { type: "geojson", data: clusterFeatureCollection([]) });
  map.addLayer({
    id: "historical-event-cluster-halo",
    type: "circle",
    source: "historical-event-clusters",
    paint: { "circle-radius": tapRadius(1, 10, 4, 18, 7, 28), "circle-color": "#f5e7d4", "circle-opacity": 0.16, "circle-blur": 0.18 }
  });
  map.addLayer({
    id: "historical-event-clusters",
    type: "circle",
    source: "historical-event-clusters",
    paint: {
      "circle-radius": tapRadius(1, 5, 4, 9, 7, 14),
      "circle-color": ["get", "color"],
      "circle-stroke-width": tapRadius(1, 1, 4, 1.5, 7, 2),
      "circle-stroke-color": "#f5e7d4"
    },
    layout: { "circle-sort-key": ["get", "startYear"] }
  });
  if (map.getStyle().glyphs) {
    map.addLayer({
      id: "historical-event-cluster-count",
      type: "symbol",
      source: "historical-event-clusters",
      layout: { "text-field": ["to-string", ["get", "clusterCount"]], "text-size": 11, "text-allow-overlap": true, "text-ignore-placement": true },
      paint: { "text-color": "#171914", "text-halo-color": "#f5e7d4", "text-halo-width": 0.4 }
    });
  }
  map.addSource("historical-event-spider-lines", { type: "geojson", data: spiderLineFeatureCollection(undefined) });
  map.addLayer({
    id: "historical-event-spider-lines",
    type: "line",
    source: "historical-event-spider-lines",
    paint: { "line-color": ["get", "color"], "line-opacity": 0.56, "line-width": 1.2 }
  });
  map.addSource("historical-event-spider-hover-area", { type: "geojson", data: spiderAreaFeatureCollection(undefined) });
  map.addLayer({
    id: "historical-event-spider-hover-area",
    type: "circle",
    source: "historical-event-spider-hover-area",
    paint: { "circle-radius": SPIDER_HOVER_RADIUS_PX, "circle-color": "#f5e7d4", "circle-opacity": 0.01 }
  });
  map.addSource("historical-event-spider", { type: "geojson", data: eventFeatureCollection([]) });
  map.addLayer({
    id: "historical-event-spider-halo",
    type: "circle",
    source: "historical-event-spider",
    paint: { "circle-radius": tapRadius(1, 7, 4, 12, 7, 17), "circle-color": ["get", "color"], "circle-opacity": 0.18, "circle-blur": 0.12 }
  });
  map.addLayer({
    id: "historical-event-spider",
    type: "circle",
    source: "historical-event-spider",
    paint: { "circle-radius": tapRadius(1, 3, 4, 5, 7, 7), "circle-color": ["get", "color"], "circle-stroke-width": tapRadius(1, 0.75, 4, 1.25, 7, 2), "circle-stroke-color": "#f5e7d4" },
    layout: { "circle-sort-key": ["get", "startYear"] }
  });
  map.addSource("historical-event-selection", { type: "geojson", data: featureCollection([]) });
  map.addLayer({
    id: "historical-event-selection-halo",
    type: "circle",
    source: "historical-event-selection",
    paint: { "circle-radius": tapRadius(1, 10, 4, 17, 7, 25), "circle-color": "#f5e7d4", "circle-opacity": .22, "circle-blur": .2 }
  });
  map.addLayer({
    id: "historical-event-selection",
    type: "circle",
    source: "historical-event-selection",
    paint: { "circle-radius": tapRadius(1, 5, 4, 8, 7, 11), "circle-color": ["get", "color"], "circle-stroke-width": tapRadius(1, 1.25, 4, 2, 7, 3), "circle-stroke-color": "#f5e7d4" }
  });
  styleReady = true;
  applyMapLabelLanguage();
  void addPersistedOHMBoundaries();
  if (!eventInteractionsAdded) {
    eventInteractionsAdded = true;
    map.on("click", "historical-events", (event: any) => {
      const id = event.features?.[0]?.properties?.eventId;
      const record = events.find((candidate) => candidate.id === id);
      if (record) selectRecord(record, { flyToRecord: false, syncYear: false });
    });
    map.on("click", "historical-event-spider", (event: any) => {
      const id = event.features?.[0]?.properties?.eventId;
      const record = events.find((candidate) => candidate.id === id);
      if (record) selectRecord(record, { flyToRecord: false, syncYear: false });
    });
    map.on("click", "historical-event-clusters", (event: any) => {
      const clusterKey = event.features?.[0]?.properties?.clusterKey;
      setActiveSpiderCluster(clusterKey);
    });
    map.on("click", "historical-event-selection", () => {
      const record = events.find((candidate) => candidate.id === selectedRecordId);
      if (record) showRecord(record);
    });
    map.on("mouseenter", "historical-events", (event: any) => {
      const id = event.features?.[0]?.properties?.eventId;
      const record = events.find((candidate) => candidate.id === id);
      if (record) showMapTooltip(record, event.lngLat);
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "historical-events", () => { mapTooltip.remove(); map.getCanvas().style.cursor = ""; });
    map.on("mouseenter", "historical-event-clusters", (event: any) => {
      const clusterKey = event.features?.[0]?.properties?.clusterKey;
      const cluster = clusterFromKey(clusterKey);
      if (!cluster) return;
      setActiveSpiderCluster(cluster.key);
      showClusterTooltip(cluster, event.lngLat);
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseenter", "historical-event-spider", (event: any) => {
      const id = event.features?.[0]?.properties?.eventId;
      const record = events.find((candidate) => candidate.id === id);
      if (record) showMapTooltip(record, event.lngLat);
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "historical-event-spider", () => { mapTooltip.remove(); });
    map.on("mouseenter", "historical-event-spider-hover-area", () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", "historical-event-spider-hover-area", () => {
      setActiveSpiderCluster(null);
      mapTooltip.remove();
      map.getCanvas().style.cursor = "";
    });
    map.on("mouseenter", "historical-event-selection", (event: any) => {
      const record = events.find((candidate) => candidate.id === selectedRecordId);
      if (record) showMapTooltip(record, event.lngLat);
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "historical-event-selection", () => { mapTooltip.remove(); map.getCanvas().style.cursor = ""; });
  }
  refresh();
  map.once("idle", refresh);
});

map.on("move", () => {
  if (activeSpiderClusterKey && styleReady && map.getSource("historical-event-spider")) {
    updateMapEventSources(currentVisibleMapRecords);
  }
});

async function addPersistedOHMBoundaries() {
  try {
    const response = await fetch("./data/ohm/central-europe-admin-level-2.geojson");
    if (!response.ok) throw new Error(String(response.status));
    const data = await response.json();
    map.addSource("ohm-persisted-boundaries", { type: "geojson", data });
    map.addLayer({
      id: "ohm-persisted-boundaries",
      type: "line",
      source: "ohm-persisted-boundaries",
      paint: { "line-color": "#f0e2c4", "line-width": 1.7, "line-opacity": 0.9 }
    });
    refresh();
  } catch {
    // The live OHM layer remains usable without this optional local snapshot.
  }
}

function eventCoordinateKey(record: ApiEvent) {
  return `${record.longitude.toFixed(COLOCATED_COORD_PRECISION)},${record.latitude.toFixed(COLOCATED_COORD_PRECISION)}`;
}

function buildEventGroups(records: ApiEvent[]) {
  const byCoordinate = new Map<string, ApiEvent[]>();
  records.forEach((record) => {
    const key = eventCoordinateKey(record);
    byCoordinate.set(key, [...(byCoordinate.get(key) ?? []), record]);
  });
  const singleRecords: ApiEvent[] = [];
  const clusters: EventCluster[] = [];
  byCoordinate.forEach((clusterRecords, key) => {
    if (clusterRecords.length === 1) {
      singleRecords.push(clusterRecords[0]);
      return;
    }
    const [longitude, latitude] = key.split(",").map(Number);
    const layerColors = new Set(clusterRecords.map((record) => record.layer_color));
    clusters.push({
      key,
      records: clusterRecords.slice().sort((a, b) => a.start_year - b.start_year || a.title.localeCompare(b.title)),
      longitude,
      latitude,
      color: layerColors.size === 1 ? clusterRecords[0].layer_color : "#d95d39",
      latestStartYear: Math.max(...clusterRecords.map((record) => record.start_year))
    });
  });
  return { singleRecords, clusters };
}

function featureCollection(records: ApiEvent[]) {
  return eventFeatureCollection(records);
}

function eventFeatureCollection(records: ApiEvent[], coordinateForRecord?: (record: ApiEvent, index: number) => [number, number]) {
  return {
    type: "FeatureCollection" as const,
    features: records.map((record, index) => ({
      type: "Feature" as const,
      properties: { eventId: record.id, label: record.title, color: record.layer_color, recordType: record.record_type, startYear: record.start_year },
      geometry: { type: "Point" as const, coordinates: coordinateForRecord ? coordinateForRecord(record, index) : [record.longitude, record.latitude] }
    }))
  };
}

function clusterFeatureCollection(clusters: EventCluster[]) {
  return {
    type: "FeatureCollection" as const,
    features: clusters.map((cluster) => ({
      type: "Feature" as const,
      properties: {
        clusterKey: cluster.key,
        clusterCount: cluster.records.length,
        color: cluster.color,
        startYear: cluster.latestStartYear
      },
      geometry: { type: "Point" as const, coordinates: [cluster.longitude, cluster.latitude] }
    }))
  };
}

function easeOutCubic(progress: number) {
  return 1 - (1 - progress) ** 3;
}

function spiderCoordinate(cluster: EventCluster, index: number, progress = 1) {
  const point = map.project([cluster.longitude, cluster.latitude]);
  const count = cluster.records.length;
  const radius = SPIDER_RADIUS_PX + Math.max(0, Math.ceil((count - 6) / 6)) * 10;
  const angle = -Math.PI / 2 + (index / count) * Math.PI * 2;
  const easedProgress = Math.max(0, Math.min(1, progress));
  const lngLat = map.unproject([
    point.x + Math.cos(angle) * radius * easedProgress,
    point.y + Math.sin(angle) * radius * easedProgress
  ]);
  return [lngLat.lng, lngLat.lat] as [number, number];
}

function spiderFeatureCollection(cluster: EventCluster | undefined, progress = 1) {
  if (!cluster) return eventFeatureCollection([]);
  return eventFeatureCollection(cluster.records, (_record, index) => spiderCoordinate(cluster, index, progress));
}

function spiderLineFeatureCollection(cluster: EventCluster | undefined, progress = 1) {
  return {
    type: "FeatureCollection" as const,
    features: cluster ? cluster.records.map((record, index) => ({
      type: "Feature" as const,
      properties: { color: record.layer_color },
      geometry: {
        type: "LineString" as const,
        coordinates: [[cluster.longitude, cluster.latitude], spiderCoordinate(cluster, index, progress)]
      }
    })) : []
  };
}

function spiderAreaFeatureCollection(cluster: EventCluster | undefined) {
  return {
    type: "FeatureCollection" as const,
    features: cluster ? [{
      type: "Feature" as const,
      properties: { clusterKey: cluster.key },
      geometry: { type: "Point" as const, coordinates: [cluster.longitude, cluster.latitude] }
    }] : []
  };
}

function updateMapEventSources(visibleRecords: ApiEvent[]) {
  currentVisibleMapRecords = visibleRecords;
  const { singleRecords, clusters } = buildEventGroups(visibleRecords);
  currentEventClusters = clusters;
  if (activeSpiderClusterKey && !clusters.some((cluster) => cluster.key === activeSpiderClusterKey)) activeSpiderClusterKey = null;
  const updatedRenderedCluster = renderedSpiderCluster ? clusters.find((cluster) => cluster.key === renderedSpiderCluster?.key) : undefined;
  renderedSpiderCluster = updatedRenderedCluster ?? (activeSpiderClusterKey ? clusters.find((cluster) => cluster.key === activeSpiderClusterKey) : undefined);
  if (!renderedSpiderCluster) spiderAnimationProgress = 0;
  (map.getSource("historical-events") as maplibregl.GeoJSONSource | undefined)?.setData(eventFeatureCollection(singleRecords));
  (map.getSource("historical-event-clusters") as maplibregl.GeoJSONSource | undefined)?.setData(clusterFeatureCollection(clusters));
  renderSpiderSources();
}

function renderSpiderSources() {
  const progress = easeOutCubic(spiderAnimationProgress);
  (map.getSource("historical-event-spider") as maplibregl.GeoJSONSource | undefined)?.setData(spiderFeatureCollection(renderedSpiderCluster, progress));
  (map.getSource("historical-event-spider-lines") as maplibregl.GeoJSONSource | undefined)?.setData(spiderLineFeatureCollection(renderedSpiderCluster, progress));
  (map.getSource("historical-event-spider-hover-area") as maplibregl.GeoJSONSource | undefined)?.setData(spiderAreaFeatureCollection(renderedSpiderCluster));
}

function distanceFromRecordWindow(record: ApiEvent, year: number) {
  return Math.min(Math.abs(record.start_year - year), Math.abs(record.map_end_year - year));
}

function updateMapEventDistribution(year: number, options: { notifyNew?: boolean } = {}) {
  if (!map.getSource("historical-events")) return;
  const visibleRecords = events
    .filter((event) => overlapsYear(event, year))
    .filter((event) => visibleLayerIds.has(event.layer_id));
  const visibleRecordSignature = visibleRecords.map((record) => record.id).join("|");
  if (lastVisibleRecordSignature === visibleRecordSignature) return;
  const shouldNotify = Boolean(options.notifyNew && lastVisibleRecordSignature !== undefined);
  const newlyVisibleRecords = shouldNotify
    ? visibleRecords
      .filter((record) => !lastVisibleRecordIds.has(record.id))
      .sort((a, b) => distanceFromRecordWindow(a, year) - distanceFromRecordWindow(b, year) || a.title.localeCompare(b.title))
      .slice(0, MAX_TRANSIENT_MAP_TOOLTIPS_PER_UPDATE)
    : [];
  updateMapEventSources(visibleRecords);
  lastVisibleRecordSignature = visibleRecordSignature;
  lastVisibleRecordIds = new Set(visibleRecords.map((record) => record.id));
  for (const record of newlyVisibleRecords) {
    showTransientMapTooltip(record, Math.round(year));
  }
}

function showMapTooltip(record: ApiEvent, coordinates: maplibregl.LngLatLike) {
  mapTooltip.setLngLat(coordinates).setDOMContent(tooltipContent(record)).addTo(map);
}

function showClusterTooltip(cluster: EventCluster, coordinates: maplibregl.LngLatLike) {
  const content = document.createElement("div");
  const title = document.createElement("strong");
  title.textContent = `${cluster.records.length} events here`;
  const meta = document.createElement("span");
  meta.textContent = cluster.records.slice(0, 3).map((record) => record.title).join(" · ") + (cluster.records.length > 3 ? " · …" : "");
  content.append(title, meta);
  mapTooltip.setLngLat(coordinates).setDOMContent(content).addTo(map);
}

function clusterFromKey(clusterKey: string | undefined | null) {
  if (!clusterKey) return undefined;
  return currentEventClusters.find((cluster) => cluster.key === clusterKey) ?? buildEventGroups(currentVisibleMapRecords).clusters.find((cluster) => cluster.key === clusterKey);
}

function setActiveSpiderCluster(clusterKey: string | undefined | null) {
  if (activeSpiderClusterKey === clusterKey) return;
  if (spiderAnimationFrame !== undefined) {
    cancelAnimationFrame(spiderAnimationFrame);
    spiderAnimationFrame = undefined;
  }
  const previousCluster = renderedSpiderCluster;
  activeSpiderClusterKey = clusterKey ?? null;
  const nextCluster = clusterFromKey(clusterKey) ?? previousCluster;
  if (!nextCluster || !styleReady || !map.getSource("historical-event-spider")) {
    renderedSpiderCluster = undefined;
    spiderAnimationProgress = 0;
    renderSpiderSources();
    return;
  }
  const opening = Boolean(clusterKey);
  const sameCluster = previousCluster?.key === nextCluster.key;
  renderedSpiderCluster = nextCluster;
  const from = sameCluster ? spiderAnimationProgress : 0;
  const to = opening ? 1 : 0;
  const startedAt = performance.now();
  const animate = (now: number) => {
    const elapsed = Math.min(1, (now - startedAt) / SPIDER_ANIMATION_MS);
    spiderAnimationProgress = from + (to - from) * elapsed;
    const freshCluster = clusterFromKey(nextCluster.key);
    renderedSpiderCluster = freshCluster ?? nextCluster;
    renderSpiderSources();
    if (elapsed < 1) {
      spiderAnimationFrame = requestAnimationFrame(animate);
      return;
    }
    spiderAnimationFrame = undefined;
    spiderAnimationProgress = to;
    if (!opening) {
      renderedSpiderCluster = undefined;
      renderSpiderSources();
    }
  };
  spiderAnimationFrame = requestAnimationFrame(animate);
}

function refresh(options: { applyBasemapFilter?: boolean; notifyNewMapEvents?: boolean } = {}) {
  const { applyBasemapFilter = true, notifyNewMapEvents = false } = options;
  const year = Number(yearInput.value);
  yearOutput.value = formatYear(year);
  const activeRecords = events.filter((event) => overlapsYear(event, year));
  const visibleRecords = activeRecords.filter((event) => visibleLayerIds.has(event.layer_id));
  renderTimelineTracks(year);
  const selectedPopulation = populationForYear(year);
  populationOutput.textContent = selectedPopulation ? `${formatPopulation(selectedPopulation.population)}${selectedPopulation.interpolated ? " · estimate" : ""}` : "No estimate";
  timelineNote.textContent = visibleLayerIds.size
    ? `${visibleRecords.length} event record${visibleRecords.length === 1 ? "" : "s"} overlaps ${formatYear(year)}.`
    : "Select a layer to see records that overlap this date.";
  if (!map.getSource("historical-events")) return;
  if (applyBasemapFilter && !isFallbackMap && lastMapYear !== year) {
    filterHistoricalMapByYear(year);
    // mapStatus.textContent = `OpenHistoricalMap · geography filtered to ${formatYear(year)}`;
  }
  updateMapEventDistribution(year, { notifyNew: notifyNewMapEvents });
  const selectedRecord = events.find((record) => record.id === selectedRecordId);
  const selectionSource = map.getSource("historical-event-selection") as maplibregl.GeoJSONSource | undefined;
  if (lastSelectionId !== selectedRecordId) {
    selectionSource?.setData(featureCollection(selectedRecord ? [selectedRecord] : []));
    lastSelectionId = selectedRecordId;
  }
  if (lastBordersVisible !== bordersToggle.checked) {
    for (const layer of map.getStyle().layers ?? []) {
      if (!isFallbackMap && "source-layer" in layer) {
        map.setLayoutProperty(layer.id, "visibility", bordersToggle.checked ? "visible" : "none");
      }
    }
    lastBordersVisible = bordersToggle.checked;
  }
  if (applyBasemapFilter && map.getLayer("ohm-persisted-boundaries") && lastMapYear !== year) {
    map.setLayoutProperty("ohm-persisted-boundaries", "visibility", bordersToggle.checked ? "visible" : "none");
    map.setFilter("ohm-persisted-boundaries", [
      "all",
      ["any", ["!", ["has", "startYear"]], ["<=", ["get", "startYear"], year]],
      ["any", ["!", ["has", "endYear"]], [">=", ["get", "endYear"], year]]
    ]);
  }
  if (applyBasemapFilter) lastMapYear = year;
}

function showRecord(record: ApiEvent) {
  required<HTMLElement>("record-type").textContent = recordTypeLabel(record);
  required<HTMLElement>("record-confidence").textContent = record.confidence;
  required<HTMLElement>("record-title").textContent = record.title;
  required<HTMLElement>("record-dates").textContent = record.end_year === null || record.end_year === record.start_year
    ? formatYear(record.start_year)
    : `${formatYear(record.start_year)} — ${formatYear(record.end_year)}`;
  required<HTMLElement>("record-summary").textContent = record.summary;
  const rows: Array<[string, string]> = [
    ["Layer", record.layer_name],
    ["Location", record.location_name],
    ["Coordinates", `${record.latitude.toFixed(3)}, ${record.longitude.toFixed(3)}`],
    ["Date precision", record.precision]
  ];
  if (record.record_type === "war") rows.splice(1, 0, ["Seed battles", String(record.battle_count ?? 0)]);
  if (record.record_type === "event" && record.layer_id === "war") rows.splice(1, 0, ["Part of war", record.war_title ?? "Ungrouped battle"]);
  required<HTMLElement>("record-facts").replaceChildren(...rows.flatMap(([term, detail]) => {
    const dt = document.createElement("dt"); dt.textContent = term;
    const dd = document.createElement("dd"); dd.textContent = detail;
    return [dt, dd];
  }));
  const sourceLink = required<HTMLAnchorElement>("record-source");
  sourceLink.href = record.source_url ?? "#";
  sourceLink.hidden = !record.source_url;
  sourceLink.firstChild!.textContent = record.source_publisher ? `Source: ${record.source_publisher} ` : "Source ";
  recordCard.hidden = false;
  dataDesk.hidden = true;
}

function selectRecord(record: ApiEvent, options: { flyToRecord?: boolean; syncYear?: boolean } = {}) {
  const { flyToRecord = true, syncYear = true } = options;
  selectedRecordId = record.id;
  timelineNeedsRender = true;
  if (syncYear) setCurrentYear(record.start_year);
  else refresh();
  showRecord(record);
  if (flyToRecord) {
    map.flyTo({ center: [record.longitude, record.latitude], zoom: Math.max(4, map.getZoom()), duration: 700, essential: true });
  }
}

function clearSelection() {
  selectedRecordId = null;
  timelineNeedsRender = true;
  recordCard.hidden = true;
  mapTooltip.remove();
  refresh();
}

function renderLayers() {
  eventLayers.replaceChildren(...layers.map((layer) => {
    const label = document.createElement("label");
    label.className = "layer-control";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = visibleLayerIds.has(layer.id);
    input.setAttribute("aria-label", `Toggle ${layer.name}`);
    input.addEventListener("change", () => {
      input.checked ? visibleLayerIds.add(layer.id) : visibleLayerIds.delete(layer.id);
      timelineNeedsRender = true;
      clearTransientMapTooltips();
      refresh();
    });
    const swatch = document.createElement("span");
    swatch.className = "layer-swatch event-swatch";
    swatch.style.backgroundColor = layer.color;
    const name = document.createElement("span");
    name.textContent = layer.name;
    const count = document.createElement("small");
    const total = events.filter((event) => event.layer_id === layer.id).length;
    count.textContent = `${total} record${total === 1 ? "" : "s"}`;
    label.append(input, swatch, name, count);
    return label;
  }));
  eventLayerSelect.replaceChildren(...layers.map((layer) => {
    const option = document.createElement("option");
    option.value = layer.id;
    option.textContent = layer.name;
    return option;
  }));
  eventWarSelect.replaceChildren(
    new Option("Choose a war", ""),
    ...wars.map((war) => new Option(`${war.title} (${formatYear(war.start_year)}–${formatYear(war.end_year ?? war.start_year)})`, war.id))
  );
  updateEventFormFields();
  required<HTMLButtonElement>("add-event-button").disabled = layers.length === 0;
}

function updateEventFormFields() {
  const isBattle = eventLayerSelect.value === "war";
  const isLifeEvent = eventLayerSelect.value === "life";
  warFormField.hidden = !isBattle;
  eventWarSelect.required = isBattle;
  eventEndYear.required = isBattle;
  eventEndYear.placeholder = isBattle ? "Required for a battle" : "Optional";
  lifeKindFormField.hidden = !isLifeEvent;
  eventKindSelect.required = isLifeEvent;
}

async function loadData() {
  try {
    const [layersResponse, eventsResponse, warsResponse] = await Promise.all([fetch("/api/layers"), fetch("/api/events"), fetch("/api/wars")]);
    if (!layersResponse.ok || !eventsResponse.ok || !warsResponse.ok) throw new Error("The event API did not respond.");
    layers = await layersResponse.json() as ApiLayer[];
    const eventRecords = await eventsResponse.json() as ApiEvent[];
    wars = await warsResponse.json() as ApiEvent[];
    events = [...eventRecords, ...wars];
    layers.forEach((layer) => visibleLayerIds.add(layer.id));
    timelineNeedsRender = true;
    renderLayers();
    refresh();
  } catch (error) {
    console.error(error);
    timelineNote.textContent = "Event service unavailable. Start the Worldvis server to load and add records.";
    // mapStatus.textContent = "Historical map loaded · event service unavailable";
  }
}

async function loadPopulation() {
  try {
    const response = await fetch("/api/population");
    if (!response.ok) throw new Error("The population API did not respond.");
    const population = await response.json() as PopulationResponse;
    populationPoints = population.points.sort((a, b) => a.year - b.year);
    populationSource.href = population.source.url;
    populationSource.textContent = population.live ? "OWID ↗" : "OWID fallback ↗";
    populationSource.hidden = false;
    refresh();
  } catch (error) {
    console.error(error);
    populationOutput.textContent = "Population series unavailable.";
  }
}

let animation: number | undefined;
let playbackYearStep = 1;
let lastTimelineYear = INITIAL_YEAR;
let playbackVisualYear = INITIAL_YEAR;
let playbackRefreshTimer: number | undefined;

function setTimelineWindow(center: number, span: number) {
  const boundedSpan = Math.max(12, Math.min(TIMELINE_MAX_YEAR - TIMELINE_MIN_YEAR, Math.round(span)));
  let start = Math.round(center - boundedSpan / 2);
  let end = start + boundedSpan;
  if (start < TIMELINE_MIN_YEAR) { start = TIMELINE_MIN_YEAR; end = start + boundedSpan; }
  if (end > TIMELINE_MAX_YEAR) { end = TIMELINE_MAX_YEAR; start = end - boundedSpan; }
  timelineStart = start;
  timelineEnd = end;
  yearInput.min = String(start);
  yearInput.max = String(end);
  timelineNeedsRender = true;
}

function keepYearInTrackWindow(year: number) {
  const span = timelineEnd - timelineStart;
  const margin = span * .14;
  if (year < timelineStart + margin || year > timelineEnd - margin) setTimelineWindow(year, span);
}

function setCurrentYear(year: number, options: { logPassage?: boolean } = {}) {
  const previous = Number(yearInput.value);
  const next = Math.max(TIMELINE_MIN_YEAR, Math.min(TIMELINE_MAX_YEAR, Math.round(year)));
  keepYearInTrackWindow(next);
  yearInput.value = String(next);
  lastTimelineYear = next;
  playbackVisualYear = next;
  refresh({ notifyNewMapEvents: options.logPassage !== false && previous !== next });
}

function setPlaybackYear(year: number, options: { logPassage?: boolean } = {}) {
  const previous = Number(yearInput.value);
  const next = Math.max(TIMELINE_MIN_YEAR, Math.min(TIMELINE_MAX_YEAR, Math.round(year)));
  keepYearInTrackWindow(next);
  yearInput.value = String(next);
  lastTimelineYear = next;

  // Keep the clock and the visual playhead on the rAF path. The expensive map
  // filter is coalesced after the frame, so a late map update cannot reset the
  // playback interval or make the next tick wait for it.
  if (playbackRefreshTimer !== undefined) return;
  playbackRefreshTimer = window.setTimeout(() => {
    playbackRefreshTimer = undefined;
    refresh({ notifyNewMapEvents: false });
  }, 0);
}

function stepTimeline(years: number) {
  const next = Number(yearInput.value) + years;
  const wrapped = next > TIMELINE_MAX_YEAR || next < TIMELINE_MIN_YEAR;
  setCurrentYear(next > TIMELINE_MAX_YEAR ? TIMELINE_MIN_YEAR : next < TIMELINE_MIN_YEAR ? TIMELINE_MAX_YEAR : next, { logPassage: !wrapped });
}

function togglePlayback() {
  const button = required<HTMLButtonElement>("play-button");
  if (animation) {
    cancelAnimationFrame(animation);
    animation = undefined;
    document.documentElement.classList.remove("timeline-playing");
    setCurrentYear(Math.round(playbackVisualYear));
    button.textContent = "▶";
    button.setAttribute("aria-label", "Play timeline");
    return;
  }
  button.textContent = "Ⅱ";
  button.setAttribute("aria-label", "Pause timeline");
  document.documentElement.classList.add("timeline-playing");
  let segmentStartYear = Number(yearInput.value);
  let segmentEndYear = segmentStartYear + playbackYearStep;
  let segmentStartedAt = performance.now();
  let lastLoggedVisualYear = segmentStartYear;
  const nextPlaybackYear = (year: number) => {
    const next = year + playbackYearStep;
    return next > TIMELINE_MAX_YEAR ? TIMELINE_MIN_YEAR : next < TIMELINE_MIN_YEAR ? TIMELINE_MAX_YEAR : next;
  };
  segmentEndYear = nextPlaybackYear(segmentStartYear);
  const tick = (now: number) => {
    let elapsed = now - segmentStartedAt;
    while (elapsed >= PLAYBACK_YEAR_DURATION_MS) {
      const wrapped = segmentEndYear === TIMELINE_MIN_YEAR || segmentEndYear === TIMELINE_MAX_YEAR;
      setPlaybackYear(segmentEndYear, { logPassage: false });
      if (wrapped) lastLoggedVisualYear = segmentEndYear;
      segmentStartYear = segmentEndYear;
      segmentEndYear = nextPlaybackYear(segmentStartYear);
      segmentStartedAt += PLAYBACK_YEAR_DURATION_MS;
      elapsed = now - segmentStartedAt;
    }
    const progress = Math.max(0, Math.min(1, elapsed / PLAYBACK_YEAR_DURATION_MS));
    playbackVisualYear = segmentStartYear + (segmentEndYear - segmentStartYear) * progress;
    updateTimelinePlayhead(playbackVisualYear);
    updateMapEventDistribution(playbackVisualYear, { notifyNew: Math.abs(playbackVisualYear - lastLoggedVisualYear) <= Math.max(1, playbackYearStep + 1) });
    lastLoggedVisualYear = playbackVisualYear;
    animation = requestAnimationFrame(tick);
  };
  animation = requestAnimationFrame(tick);
}

yearInput.addEventListener("input", () => {
  const next = Number(yearInput.value);
  lastTimelineYear = next;
  refresh({ notifyNewMapEvents: true });
});
bordersToggle.addEventListener("change", () => refresh());
labelNativeButton.addEventListener("click", () => {
  mapLabelLanguage = "native";
  applyMapLabelLanguage();
});
labelEnglishButton.addEventListener("click", () => {
  mapLabelLanguage = "english";
  applyMapLabelLanguage();
});
function parseJumpYear(value: string) {
  const normalized = value.trim().toUpperCase();
  const matched = normalized.match(/^(-?\d+)\s*(BCE|BC|CE|AD)?$/);
  if (!matched) return null;
  const number = Number(matched[1]);
  if (!Number.isInteger(number)) return null;
  const era = matched[2];
  const year = era === "BCE" || era === "BC" ? -Math.abs(number) : number;
  return year >= TIMELINE_MIN_YEAR && year <= TIMELINE_MAX_YEAR ? year : null;
}
function closeYearJump(commit: boolean) {
  if (yearJumpInput.hidden) return;
  const requestedYear = parseJumpYear(yearJumpInput.value);
  if (commit && requestedYear === null) {
    yearJumpInput.setAttribute("aria-invalid", "true");
    yearJumpInput.select();
    return;
  }
  if (commit && requestedYear !== null) {
    setCurrentYear(requestedYear);
  }
  yearJumpInput.hidden = true;
  yearOutput.hidden = false;
  yearJumpInput.removeAttribute("aria-invalid");
}
yearJumpTrigger.addEventListener("click", () => {
  yearJumpInput.value = String(yearInput.value);
  yearOutput.hidden = true;
  yearJumpInput.hidden = false;
  yearJumpInput.focus();
  yearJumpInput.select();
});
yearJumpInput.addEventListener("keydown", (event) => {
  if (event.key === "Enter") closeYearJump(true);
  if (event.key === "Escape") closeYearJump(false);
});
yearJumpInput.addEventListener("blur", () => closeYearJump(true));
function yearAtTrackPosition(clientX: number) {
  const bounds = trackTimeline.getBoundingClientRect();
  const timelineX = ((clientX - bounds.left) / bounds.width) * 1200;
  if (timelineX < TRACK_GUTTER || timelineX > TRACK_GUTTER + TRACK_WIDTH) return null;
  return timelineStart + ((timelineX - TRACK_GUTTER) / TRACK_WIDTH) * (timelineEnd - timelineStart);
}
let pendingScrubYear: number | undefined;
let scrubCommitTimer: number | undefined;
function scrubTracks(clientX: number) {
  const year = yearAtTrackPosition(clientX);
  if (year === null) return;
  // Move the playhead immediately, but coalesce the expensive map/style work
  // while a pointer is moving.
  playbackVisualYear = year;
  updateTimelinePlayhead(year);
  pendingScrubYear = year;
  if (scrubCommitTimer !== undefined) return;
  scrubCommitTimer = window.setTimeout(() => {
    scrubCommitTimer = undefined;
    if (pendingScrubYear !== undefined) setCurrentYear(pendingScrubYear);
    pendingScrubYear = undefined;
  }, 80);
}
function zoomTracksAt(clientX: number, factor: number) {
  const year = yearAtTrackPosition(clientX);
  if (year === null) return;
  setTimelineWindow(year, (timelineEnd - timelineStart) * factor);
  refresh();
}
function recordFromTrackTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) return undefined;
  const eventId = target.getAttribute("data-event-id");
  return eventId ? events.find((record) => record.id === eventId) : undefined;
}
let draggingTracks = false;
trackTimeline.addEventListener("pointerdown", (event) => {
  if (event.shiftKey) {
    event.preventDefault();
    if (event.button !== 2) zoomTracksAt(event.clientX, .5);
    return;
  }
  const record = recordFromTrackTarget(event.target);
  if (record) {
    selectRecord(record);
    return;
  }
  draggingTracks = true;
  trackTimeline.setPointerCapture(event.pointerId);
  scrubTracks(event.clientX);
});
trackTimeline.addEventListener("pointermove", (event) => { if (draggingTracks) scrubTracks(event.clientX); });
trackTimeline.addEventListener("pointerup", () => { draggingTracks = false; });
trackTimeline.addEventListener("pointercancel", () => { draggingTracks = false; });
trackTimeline.addEventListener("contextmenu", (event) => {
  if (!event.shiftKey) return;
  event.preventDefault();
  zoomTracksAt(event.clientX, 2);
});
trackTimeline.addEventListener("keydown", (event) => {
  if (event.key !== "Enter") return;
  const record = recordFromTrackTarget(event.target);
  if (!record) return;
  event.preventDefault();
  selectRecord(record);
});
required<HTMLButtonElement>("track-zoom-in").addEventListener("click", () => {
  const bounds = trackTimeline.getBoundingClientRect();
  zoomTracksAt(bounds.left + bounds.width / 2, .5);
});
required<HTMLButtonElement>("track-zoom-out").addEventListener("click", () => {
  const bounds = trackTimeline.getBoundingClientRect();
  zoomTracksAt(bounds.left + bounds.width / 2, 2);
});
const legendPanel = required<HTMLElement>("legend-panel");
const legendToggle = required<HTMLButtonElement>("legend-toggle");
legendToggle.addEventListener("click", () => {
  const open = !legendPanel.classList.contains("open");
  legendPanel.classList.toggle("open", open);
  legendToggle.setAttribute("aria-expanded", String(open));
});
document.addEventListener("click", (event) => {
  if (!legendPanel.classList.contains("open")) return;
  if (event.target instanceof Node && (legendPanel.contains(event.target) || legendToggle.contains(event.target))) return;
  legendPanel.classList.remove("open");
  legendToggle.setAttribute("aria-expanded", "false");
});
required<HTMLButtonElement>("play-button").addEventListener("click", togglePlayback);
required<HTMLButtonElement>("step-back-button").addEventListener("click", () => stepTimeline(-1));
required<HTMLButtonElement>("step-forward-button").addEventListener("click", () => stepTimeline(1));
document.querySelectorAll<HTMLButtonElement>("[data-year-step]").forEach((button) => {
  button.addEventListener("click", () => {
    playbackYearStep = Number(button.dataset.yearStep);
    document.querySelectorAll<HTMLButtonElement>("[data-year-step]").forEach((candidate) => {
      const active = candidate === button;
      candidate.classList.toggle("active", active);
      candidate.setAttribute("aria-pressed", String(active));
    });
  });
});
required<HTMLButtonElement>("close-card").addEventListener("click", clearSelection);
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !recordCard.hidden) clearSelection();
});
document.addEventListener("keydown", (event) => {
  const target = event.target;
  const isTextEntry = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || (target instanceof HTMLElement && target.isContentEditable);
  const isNativeButton = target instanceof HTMLElement && Boolean(target.closest("button,a"));
  if (event.code !== "Space" || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || isTextEntry || isNativeButton) return;
  event.preventDefault();
  togglePlayback();
});
// required<HTMLButtonElement>("source-button").addEventListener("click", () => {
//   dataDesk.hidden = !dataDesk.hidden;
//   if (!recordCard.hidden || selectedRecordId) clearSelection();
//   required<HTMLElement>("desk-summary").textContent = `${events.length} event records · ${layers.length} layers · SQLite-backed local source trail`;
// });
required<HTMLButtonElement>("close-desk").addEventListener("click", () => { dataDesk.hidden = true; });
required<HTMLButtonElement>("add-event-button").addEventListener("click", () => {
  formError.textContent = "";
  eventDialog.showModal();
});
eventLayerSelect.addEventListener("change", updateEventFormFields);
required<HTMLButtonElement>("close-event-dialog").addEventListener("click", () => eventDialog.close());
eventForm.addEventListener("submit", async (submitEvent) => {
  submitEvent.preventDefault();
  formError.textContent = "";
  const formData = new FormData(eventForm);
  const payload = Object.fromEntries(formData.entries());
  try {
    const response = await fetch("/api/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = await response.json() as ApiEvent | { error: string };
    if (!response.ok || "error" in body) throw new Error("error" in body ? body.error : "Could not save this event.");
    events.push(body);
    visibleLayerIds.add(body.layer_id);
    renderLayers();
    eventForm.reset();
    updateEventFormFields();
    eventDialog.close();
    refresh();
    showRecord(body);
  } catch (error) {
    formError.textContent = error instanceof Error ? error.message : "Could not save this event.";
  }
});

void loadData();
void loadPopulation();
